import { useContext, useEffect, useRef, useState } from "react";
import { MuteReason, TelephonyContext } from "../telephony/TelephonyContext";
import { text } from "../../utils/i18n";
import ActionNotification, { HideNotificationCallback } from "../core/ActionNotification/ActionNotification";
import { DoubleKeyPressHandler } from "../../models/DoubleClickHandler";
import "./KeyboardShortcutHandler.scss";
import { useForegroundInterval } from "../hooks";
import { KeyboardShortcuts, KeyboardShortcutContext as KeyboardShortcutContext } from "./KeyboardShortcutContextProvider";
import { useHistory, useLocation } from "react-router";
import { getKeyFromEvent } from "../../utils/keyboard";
import { TelephonyServiceStatus } from "../telephony/TelephonyService";
import { getEstimatedTimeToReadString } from "../../utils/semantic";
import { KeyboardKey } from "../KeyboardKey/KeyboardKey";

/**
 * Handles keyboard presses as shortcuts. Can either execute a custom function or call `.click` on a matching element.
 * 
 * Elements may use the `data-ks` attribute.
 * When a keyboard shortcut is made, the shortcut has an identifier such as "call-mute". If an element exists
 * with a matching `data-ks`, then that element will be clicked. If there is no matching element then this component
 * may, in addition to clicking on an element, execute a custom function.
 * 
 * Relevant attributes:
 * - `data-ks`: identifies a keyboard shortcut. Element will be clicked.
 * - `data-ks-key`: Set automatically by this component. Will show an indicator on a child element with `data-ks-indicator` set.
 * - `data-ks-indicator`: Use this attribute to specify which child element should render the indicator as an `::after`.
 * - `data-ks-click`: Will be briefly set on the clicked element (data-ks) during onClick to signal that the element was clicked on via the keyboard. Use `isClickedViaKeyboardShortcut` to check this.
 */
export const KeyboardShortcutHandler: React.FC = () => {
  const telephonyContext = useContext(TelephonyContext);
  const hangUpCallHandler = useRef(new DoubleKeyPressHandler(text`Tryck två gånger för att lägga på`));
  const [nonce, setNonce] = useState(0);
  const [isAltKeyPressed, setAltKeyPressed] = useState(false);
  const keyboardShortcuts = useContext(KeyboardShortcutContext);
  const hidePreviousNotification = useRef<HideNotificationCallback>(null);
  const history = useHistory();
  const location = useLocation();

  //  För att visa indikator på html-element så behöver vi uppdatera indikatorn när element sporadiskt läggs till. 
  useForegroundInterval(() => {
    setNonce(Date.now());
  }, { delay: 1000 });

  function showNotification(message: string, event: KeyboardEvent) {
    if (true) {
      //  TODO: Fixa till mig. Den ska dölja den förra notisen direkt och det blir konstigt om man spammar notisen, pga en "minShowTime".
      //  Istället spelas en animation "clickedViaKeyboard" på knappen nu.
      return;
    }

    const children = (
      <>
        <KeyboardKey value={getKeyFromEvent(event)} />
        <span style={{ marginLeft: 8 }}>
          {message}
        </span>
      </>
    );

    hidePreviousNotification.current?.();
    hidePreviousNotification.current =
      ActionNotification.show(children, 'none', false, getEstimatedTimeToReadString(message) * 0.33);
  }

  function getElement(attribute: string): HTMLElement {
    //  Följ en prioriteringsordning. Först saker i öppna dropdowns osv. 
    return document.querySelector(`#dropdowns [data-open] [data-ks="${attribute}"]`) ??
      document.querySelector(`[data-ks="${attribute}"]`);
  }

  const currentActions = useRef<{
    /**
     * Value of `data-ks` for matching element.
     */
    attribute?: string;
    key: string;
    onElementAbsent?: (event: KeyboardEvent) => void;
  }[]>([]);

  useEffect(() => {
    //  Återställ.
    currentActions.current.forEach(action => {
      if (action.attribute) {
        getElement(action.attribute)?.removeAttribute('data-ks-key');
      }
    });

    currentActions.current = [];

    if (!keyboardShortcuts.settings.isEnabled) {
      return;
    }

    //  Generell.
    currentActions.current.push({ key: "Enter", attribute: "submit" });

    if (telephonyContext?.getCurrentCallSession() && !telephonyContext.getCurrentCallSession().disconnectedAt) {
      currentActions.current.push({
        key: keyboardShortcuts.settings.actions["call-mute"],
        attribute: "call-mute",
        onElementAbsent: event => {
          telephonyContext.setState(state => {
            if (state.muteReason === MuteReason.PlayingCallNotification) {
              return {};
            }

            showNotification(state.muteReason ? text`Mikrofon på` : text`Mikrofon av`, event);

            return { muteReason: state.muteReason ? null : MuteReason.MutedByUser };
          });
        }
      });
    }

    if (telephonyContext?.getCurrentCallSession()?.isActive) {
      currentActions.current.push({
        key: keyboardShortcuts.settings.actions["call-hang-up"],
        attribute: "call-hang-up", //  Använd knappen om den finns, annars kör `fn` bara.
        onElementAbsent: event => {
          if (hangUpCallHandler.current.click()) {
            telephonyContext.hangUp();
            showNotification(text`Lagt på samtalet`, event);
          }
        },
      });
    }

    if (telephonyContext) {
      //  Kan använda för att börja ringa också.
      currentActions.current.push({
        key: keyboardShortcuts.settings.actions["call-big-view"],
        onElementAbsent: event => {
          if (telephonyContext.serviceStatus === TelephonyServiceStatus.Connected) {
            history.push('/contacts/session')

          } else {
            telephonyContext.setState({ isTelephonyWidgetOpen: true });
          }
        }
      });
    }

    for (const number of [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]) {
      const asListIndex = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0].indexOf(number);

      //  Default sak som ska hanteras. data-ks={index}
      currentActions.current.push({
        key: String(number),
        attribute: String(asListIndex),
      });
    }

    if (telephonyContext) {
      const callUtilitiesIds: (keyof KeyboardShortcuts)[] = [
        'call-0',
        'call-1',
        'call-2',
        'call-3',
        'call-4',
        'call-5',
        'call-6',
        'call-7',
        'call-8',
        'call-9',
      ];

      for (let i = 0; i < callUtilitiesIds.length; i++) {
        const key = keyboardShortcuts.settings.actions[callUtilitiesIds[i]];

        if (telephonyContext.getCurrentCallSession()?.isActive || isAltKeyPressed) {
          currentActions.current.push({
            key,
            attribute: "call-action-" + i,
          });

        } else {
          currentActions.current.push({
            key,
            attribute: "select-call-tag-" + i,
          });
        }
      }
    }

    //  TODO: Om flera actions har samma key så ska den senaste gälla.

    if (keyboardShortcuts.settings.isIndicatorEnabled) {
      //  Sätt ett attribute på elementen som visar vilken tangent dom har.
      currentActions.current.forEach(action => {
        if (action.attribute) {
          //  Upper case eftersom måste matcha exakt som i KeyboardShortcutHandler.
          getElement(action.attribute)?.setAttribute('data-ks-key', action.key.toUpperCase());
        }
      });
    }
  }, [
    location.pathname,
    location.hash,
    keyboardShortcuts._nonce,
    keyboardShortcuts.settings.isEnabled,
    keyboardShortcuts.settings.isIndicatorEnabled,
    isAltKeyPressed,
    nonce,
    telephonyContext?.getCurrentCallSession()?.isActive,
  ]);

  function onPress(event: KeyboardEvent) {
    for (const action of currentActions.current) {
      if (getKeyFromEvent(event).toUpperCase() !== action.key.toUpperCase()) {
        continue;
      }

      const element = action.attribute ? getElement(action.attribute) : null;

      let handled = false;

      //  Prioritera att kalla .click på ett existerande element. Då kan elementet ge visuell feedback, exempelvis när dubbeltryck på påkrävt.
      if (element) {
        //  Sätt ett attribut så att knappen / etc. kan göra någonting annorlunda när det är via tangentbordet, exempelvis påtvinga dubbeltryck och ändra knappen visuellt.
        //  .click propagerar inte till children så elementet med onClick behöver markeras på något sätt.
        const clickableElement = element.querySelector('[role="button"]') as HTMLElement ?? element;

        clickableElement.setAttribute('data-ks-click', 'true');
        clickableElement.click();

        setTimeout(() => clickableElement.removeAttribute('data-ks-click'), 50);

        handled = true;

      } else if (action.onElementAbsent) {
        action.onElementAbsent(event);

        handled = true;
      }

      if (handled) {
        event.stopPropagation();
        event.preventDefault();
        break;
      }
    }
  }

  useEffect(() => {
    if (!keyboardShortcuts.settings.isEnabled) {
      return;
    }

    const keyup = (event: KeyboardEvent) => {
      setAltKeyPressed(event.altKey);
    };

    const keydown = (event: KeyboardEvent) => {
      setAltKeyPressed(event.altKey);

      if (event.key === "Alt") {
        //  Den fokuserar på något i Chrome-inställningarna istället.
        event.preventDefault();
        return;
      }

      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      try {
        onPress(event);

      } catch (error) {
        console.error("onPress error", error);
      }
    };

    document.addEventListener('keydown', keydown);
    document.addEventListener('keyup', keyup);

    return () => {
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('keyup', keyup);
    };
  }, [keyboardShortcuts.settings.isEnabled]);

  return null;
}

export function isClickedViaKeyboardShortcut(element: HTMLElement) {
  return element.getAttribute('data-ks-click') === 'true';
}